import React, { PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './PopularCards.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { sanitizeAuthorInput, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { StarArray, IReviewData, fetchBatchBVData } from '../../foundation/StarArray/StarArray';
import { useWidthContext } from '../../providers/Width';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { TCreditCard } from '../../types/models/CreditCard';
import { Button, Row, Col, Title, ToolTip, Section, List } from '../../importCL';

const DEFAULT_IMAGE_WIDTH = 288;
const DEFAULT_IMAGE_HEIGHT = 182;

type TAlternateDisplayCardProps =
    IPopCard
    & IEqualizeData
    & {
        bazaarvoiceData?: IReviewData,
        tooltip?: string,
        showingReviews?: boolean,
        alternateStyle?: boolean,
        cardInfo?: TCreditCard,
        onLoad?: () => void,
        style?: React.CSSProperties,
    };


interface IEqualizeData {
    titleHeight: number;
    subtitleHeight: number;
}

interface IPopCard {
    cardImage: string;
    cardImageAltText: string;
    cardTitle: string;
    bazaarvoiceProductId: string;
    bazaarvoiceStarsLink: string;
    bazaarvoicestarsLinkTarget: string;
    cardDescription: string;
    cardDetailsLabelText: string;
    cardDetailsLink: string;
    cardDetailsParams: string;
    cardDetailsCtaTarget: string;
    pqCtaLabel: string;
    pqLink: string;
    pqLinkTarget: string;
    pqLinkParams: string;
}

interface IPopularCardsProps extends MappedComponentProperties {
    alternateStyle?: boolean;
    sectionTitle?: string;
    sectionLinkLabel?: string;
    sectionLinkPath?: string;
    showingReviews?: boolean;
    tooltip?: string;
    cards?: IPopCard[];
    cardInfo?: TCreditCard[];
}

export const PopularCardsEditConfig = {
    emptyLabel: 'PopularCards',
    isEmpty: (props: PropsWithChildren<IPopularCardsProps>) => {
        return !props || !props.cards || !props.cards.length;
    },
};

function displayCardRecommendedFeatures(cardInfo: TCreditCard, centered: boolean = false) {
    const listOfFeatures: string[] = [];

    if (cardInfo.recommendedFeatures.length > 0) {
        listOfFeatures[0] = cardInfo.recommendedFeatures[0];
    }
    if (cardInfo.recommendedFeatures.length > 2) {
        listOfFeatures[1] = cardInfo.recommendedFeatures[1];
    }

    return (
        <div className={'PopularCards__alternate__recommendedFeatures ' + (centered ? 'PopularCards__alternate__recommendedFeatures--centered' : '')}>
            <div className='PopularCards__alternate__recommendedFeatures__title'>Recommended for :</div>
            <List items={listOfFeatures} checks />
        </div>
    );
}

function displayCardReviews(props: TAlternateDisplayCardProps, mainCard: boolean = false) {
    return (
        <>
            {props.bazaarvoiceStarsLink && props.bazaarvoiceData && (
                <>
                    <LinkWrapper href={props.bazaarvoiceStarsLink}>
                        <section className={mainCard ? 'PopularCards__alternate__column__reviews--noPadding' : 'PopularCards__alternate__column__reviews'}>
                            <StarArray {...props.bazaarvoiceData} />
                            <div className='PopularCards__alternate__column__reviewsText'>
                                {' ' + (props.bazaarvoiceData.average.toFixed(1))} / {props.bazaarvoiceData.overall}
                            </div>
                        </section>
                    </LinkWrapper>
                    {props.bazaarvoiceData &&
                        <div className={mainCard ? 'star-rating-text--noPadding' : 'star-rating-text'}>
                            Based on <LinkWrapper href={props.bazaarvoiceStarsLink}>{props.bazaarvoiceData.total.toLocaleString('en-US')} reviews</LinkWrapper>{' '}
                            {props.tooltip && (
                                <ToolTip tabIndex={0} className='star-rating-tooltip' mobileNotchPosition='top-right' notchPosition='top-right'>
                                    <div>{transformHTMLParserLinks(props.tooltip)}</div>
                                </ToolTip>
                            )}
                        </div>
                    }
                </>
            ) || (
                <section className='PopularCards__alternate__column__reviews'>
                    {props.bazaarvoiceData && props.showingReviews && (
                        <>
                            <StarArray {...props.bazaarvoiceData} />
                            <div className='PopularCards__alternate__column__reviewsText'>
                                {' ' + (props.bazaarvoiceData.average.toFixed(1))} / {props.bazaarvoiceData.overall}
                            </div>
                            <div className={mainCard ? 'star-rating-text--noPadding' : 'star-rating-text'}>
                                    Based on {props.bazaarvoiceData.total.toLocaleString('en-US')} reviews{' '}
                                {props.tooltip && (
                                    <ToolTip tabIndex={0} className='star-rating-tooltip' mobileNotchPosition='top-right' notchPosition='top-right'>
                                        <div>{transformHTMLParserLinks(props.tooltip)}</div>
                                    </ToolTip>
                                )}
                            </div>
                        </>
                    )}
                </section>
            )}
        </>
    );
}

function displayButtonLinks(props: TAlternateDisplayCardProps) {
    return (
        <p className='PopularCards__alternate__buttonContainer'>
            <Button variant='solid' size='medium' className={`PopularCards__alternate__button ${!props.pqCtaLabel ? 'PopularCards__alternate__button--emptyLabel': ''}`} href={`${props.pqLink}${props.pqLinkParams ? props.pqLinkParams : ''}`}>
                {transformHTMLParserLinks(sanitizeAuthorInput(props.pqCtaLabel, ['<span>'])) as unknown as ReactElement}
            </Button>
            <Button variant='link' icon='chevron' size='medium' className='PopularCards__alternate__button' href={`${props.cardDetailsLink}${props.cardDetailsParams ? props.cardDetailsParams : ''}`} >
                {transformHTMLParserLinks(props.cardDetailsLabelText) as unknown as ReactElement}
            </Button>
        </p>);
}

function displayCardTitle(props: TAlternateDisplayCardProps, mainCard: boolean = false) {
    return (
        <Title tag='h3' className={'h5 PopularCards__alternate__column__title' + (mainCard ? '--mainTitle' : '')}>
            <LinkWrapper href={props.cardDetailsLink} style={{ height: props.titleHeight ? props.titleHeight : 'auto' }}>
                {transformHTMLParserLinks(props.cardTitle)}
            </LinkWrapper>
        </Title>
    );
}

function displayCardImage(props: TAlternateDisplayCardProps, main: boolean = false, onLoad?: () => void) {
    const fallbackWidth = main ? 340 : DEFAULT_IMAGE_WIDTH;
    const fallbackHeight = main ? 215 : DEFAULT_IMAGE_HEIGHT;

    return (
        <div className={'PopularCards__alternate__column__image' + (main ? '--mainImage' : '') + (!(props.bazaarvoiceStarsLink && props.showingReviews) ? 'NoReviews' : '')}>
            <LinkWrapper href={`${props.pqLink}${props.pqLinkParams ? props.pqLinkParams : ''}`}>
                <img loading='lazy' src={props.cardImage} alt={props.cardImageAltText} width={fallbackWidth} height={fallbackHeight} onLoad={onLoad} />
            </LinkWrapper>
        </div>
    );
}

const SideCard = (props: TAlternateDisplayCardProps): JSX.Element => {
    return (<Col as='article' className='PopularCards__alternate__column'>
        <div className='PopularCards__alternate__container'>
            <div>
                {displayCardTitle(props)}
                {displayCardRecommendedFeatures(props.cardInfo)}
            </div>
            <div className='PopularCards__alternate__cardAndReviews'>
                {displayCardImage(props)}
                {props.bazaarvoiceStarsLink && props.showingReviews && displayCardReviews(props)}
            </div>
        </div>
        {displayButtonLinks(props)}
    </Col>);
};

const MainCard = (props: TAlternateDisplayCardProps): JSX.Element => {
    return (<article className='Col PopularCards__alternate__column' style={props.style}>
        <div className='PopularCards__alternate__column--mainCard'>
            {displayCardTitle(props, true)}
            {props.bazaarvoiceStarsLink && props.showingReviews && displayCardReviews(props, true)}
            {displayCardImage(props, true)}
            {displayCardRecommendedFeatures(props.cardInfo, true)}
            {displayButtonLinks(props)}
        </div>
    </article>);
};

const Card = (props: TAlternateDisplayCardProps): JSX.Element => {

    const { width } = useWidthContext();

    return (
        <Col as='article' xs={6} md={true} className='PopularCards__column'>
            <Title tag='h3' align='center' className='h5 PopularCards__column__title'>
                <LinkWrapper href={props.cardDetailsLink} style={{ height: props.titleHeight ? props.titleHeight : 'auto' }}>
                    {transformHTMLParserLinks(sanitizeAuthorInput(props.cardTitle, ['br', 'span']))}
                </LinkWrapper>
            </Title>
            <div className='PopularCards__column__image'>
                <LinkWrapper href={`${props.pqLink}${props.pqLinkParams ? props.pqLinkParams : ''}`}>
                    <img loading='lazy' src={props.cardImage} alt={props.cardImageAltText} width={DEFAULT_IMAGE_WIDTH} height={DEFAULT_IMAGE_HEIGHT} />
                </LinkWrapper>
            </div>
            <Title tag='h4' className='h6 PopularCards__column__subtitle' align='center'
                style={{ height: (props.subtitleHeight && (width > 768)) ? props.subtitleHeight : '' }}>{transformHTMLParserLinks(sanitizeAuthorInput(props.cardDescription, ['br']))}</Title>
            {props.bazaarvoiceStarsLink && props.showingReviews && (
                <>
                    <LinkWrapper href={props.bazaarvoiceStarsLink} target={props.bazaarvoicestarsLinkTarget}>
                        <section className='PopularCards__column__reviews'>
                            {props.bazaarvoiceData && (
                                <StarArray {...props.bazaarvoiceData} />
                            )}
                        </section>
                    </LinkWrapper>
                    {props.bazaarvoiceData &&
                        <div className='star-rating-text'>
                            {props.bazaarvoiceData.average.toFixed(1)} out of {props.bazaarvoiceData.overall} stars
                            <br />
                            based on {props.bazaarvoiceData.total.toLocaleString('en-US')} reviews{' '}
                            {props.tooltip && (
                                <ToolTip tabIndex={0} className='star-rating-tooltip' mobileNotchPosition='top-right'>
                                    <div>{transformHTMLParserLinks(props.tooltip)}</div>
                                </ToolTip>
                            )}
                        </div>
                    }
                </>
            ) || (
                <section className='PopularCards__column__reviews'>
                    {props.bazaarvoiceData && props.showingReviews && (
                        <>
                            <StarArray {...props.bazaarvoiceData} />
                            <div className='star-rating-text'>
                                {props.bazaarvoiceData.average.toFixed(1)} out of {props.bazaarvoiceData.overall} stars
                                <br />
                                based on {props.bazaarvoiceData.total.toLocaleString('en-US')} reviews{' '}
                                {props.tooltip && (
                                    <ToolTip tabIndex={0} className='star-rating-tooltip' mobileNotchPosition='top-right'>
                                        <div>{transformHTMLParserLinks(props.tooltip)}</div>
                                    </ToolTip>
                                )}
                            </div>
                        </>
                    )}
                </section>
            )}
            <hr/>
            <p>
                <LinkWrapper href={`${props.cardDetailsLink}${props.cardDetailsParams ? props.cardDetailsParams : ''}`}
                    target={props.cardDetailsCtaTarget} aria-label={'Learn more about ' + (props.cardTitle ? props.cardTitle.replace(/<\/?[^>]+(>|$)/g, '') : 'this card')}>{transformHTMLParserLinks(sanitizeAuthorInput(props.cardDetailsLabelText, ['span', 'br']))}</LinkWrapper>
                &nbsp;|&nbsp;
                <LinkWrapper href={`${props.pqLink}${props.pqLinkParams ? props.pqLinkParams : ''}`}
                    target={props.pqLinkTarget} aria-label={'See if you pre-qualify for ' + (props.cardTitle ? props.cardTitle.replace(/<\/?[^>]+(>|$)/g, '') : 'this card')}>{transformHTMLParserLinks(sanitizeAuthorInput(props.pqCtaLabel, ['span', 'br']))}</LinkWrapper>
            </p>
        </Col>
    );
};

const PopularCards = (props: PropsWithChildren<IPopularCardsProps>): JSX.Element => {
    const [titleHeight, setTitleHeight] = useState(0);
    const [subtitleHeight, setSubtitleHeight] = useState(0);
    const { width } = useWidthContext();
    const [data, setData] = useState<IReviewData[]>([{ productId: '', average: 0, total: 0, overall: 0 }]);
    const [alternateMainCardHeight, setAlternateMainCardHeight] = useState<number>();
    const alternateSecondaryColumnRef = useRef<HTMLDivElement>(null);
 
    const getEqualizedHeights = () => {
        let tallestTitle = 0;
        let tallestSubtitle = 0;
        document.querySelectorAll('.PopularCards__column__title').forEach(el => {
            if (el.clientHeight > tallestTitle) {
                tallestTitle = el.clientHeight;
            }
        });
        document.querySelectorAll('.PopularCards__column__subtitle').forEach(el => {
            if (el.clientHeight > tallestSubtitle) {
                tallestSubtitle = el.clientHeight;
            }
        });
        setTitleHeight(tallestTitle);
        setSubtitleHeight(tallestSubtitle);
    };

    useEffect(() => {
        getEqualizedHeights();
        const bvProductIds = props.cards?.reduce((ids: string[], card: any) => {
            if (card.bazaarvoiceProductId) {
                ids.push(card.bazaarvoiceProductId);
            }
            return ids;
        }, []) || [];

        fetchBatchBVData(bvProductIds).then(setData);
    }, []);

    return (
        <>
            {props.alternateStyle &&
                <Section onLoad={() => {getEqualizedHeights(); setAlternateMainCardHeight(alternateSecondaryColumnRef.current?.clientHeight);}} id='popular-cards' className='PopularCards__alternate' containerClass='container-padding' bgColor='white'
                horizontalTitle    
                title={transformHTMLParserLinks(props.sectionTitle)}
                    titleClass='ml-4 justify-self-start'>
                    <Row className='PopularCards__alternate__row' align='center'>
                        {width >= 769 &&
                            <>
                                <div className='PopularCards__alternate__row__mainCardContainer'>
                                    {props.cards.map((card, index) => {
                                        if (index === 0) {
                                            const bazaarvoiceData = data.find(d => d.productId === card.bazaarvoiceProductId);
                                            return (<MainCard {...card} style={alternateMainCardHeight ? {height: `${alternateMainCardHeight}px`} : {}} bazaarvoiceData={bazaarvoiceData} titleHeight={titleHeight} subtitleHeight={subtitleHeight} showingReviews={props.showingReviews} tooltip={props.tooltip} alternateStyle={props.alternateStyle} cardInfo={props.cardInfo[index]}
                                                key={index}></MainCard>);
                                        }
                                    })}
                                </div>
                                <div className='Col PopularCards__alternate__column--alternateContainer' ref={alternateSecondaryColumnRef}>
                                    {props.cards.map((card, index) => {
                                        if (index !== 0) {
                                            const bazaarvoiceData = data.find(d => d.productId === card.bazaarvoiceProductId);
                                            return (<SideCard {...card} bazaarvoiceData={bazaarvoiceData} titleHeight={titleHeight} subtitleHeight={subtitleHeight} showingReviews={props.showingReviews} tooltip={props.tooltip} alternateStyle={props.alternateStyle} cardInfo={props.cardInfo[index]}
                                                key={index} onLoad={() => setAlternateMainCardHeight(alternateSecondaryColumnRef.current?.clientHeight)}></SideCard>);
                                        }
                                    })}
                                </div>
                            </>
                        }
                        {width < 769 &&
                            <>
                                {props.cards.map((card, index) => {
                                    const bazaarvoiceData = data.find(d => d.productId === card.bazaarvoiceProductId);
                                    return (<MainCard {...card} bazaarvoiceData={bazaarvoiceData} titleHeight={titleHeight} subtitleHeight={subtitleHeight} showingReviews={props.showingReviews} tooltip={props.tooltip} alternateStyle={props.alternateStyle} cardInfo={props.cardInfo[index]}
                                        key={index}></MainCard>);
                                })}
                            </>
                        }
                    </Row>
                    <a href={props.sectionLinkPath}>
                        <Button variant='ghosted-dark' size='medium' className='mt-8'>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.sectionLinkLabel)) as unknown as ReactElement}
                        </Button>
                    </a>
                </Section>
            }
            {!props.alternateStyle &&
                <Section onLoad={() => getEqualizedHeights()} id='popular-cards' className='PopularCards' containerClass='container-padding' bgColor='white'
                    title={props.sectionTitle}
                    titleClass='PopularCards__title--margin'>
                    <Row className='PopularCards__row' align='center'>
                        {props.cards?.map((card, index) => {
                            const bazaarvoiceData = data.find(d => d.productId === card.bazaarvoiceProductId);
                            return (<Card {...card} bazaarvoiceData={bazaarvoiceData} titleHeight={titleHeight} subtitleHeight={subtitleHeight} showingReviews={props.showingReviews} tooltip={props.tooltip}
                                key={index}></Card>);
                        })}
                    </Row>
                    <a href={props.sectionLinkPath}><Button variant='solid'
                        size='medium' icon='chevron'
                        className='mt-8'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.sectionLinkLabel)) as unknown as ReactElement}</Button></a>
                </Section>
            }
        </>
    );
};

export default PopularCards;
